import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/auth.store';

export const REALTIME_EVENT = 'realtime:signal';

export type RealtimeSignalKind =
  | 'REPORT_SUBMITTED'
  | 'REPORT_REVIEWED'
  | 'INSPECTION_SUBMITTED'
  | 'INSPECTION_REVIEWED'
  // B6.19 — KPIs that previously changed with no signal at all (B6.18 RT-1).
  | 'INSPECTION_ASSIGNED'
  | 'INSPECTION_STARTED'
  | 'CUSTOMER_DECISION'
  | 'LIFECYCLE_CHANGED'
  // D-112 Stage 4 — an authorization case's journey changed. One coarse kind for
  // every transition; see backend `realtime.contract.ts` for why it carries no
  // authorizationId, owner or waiting party.
  | 'AUTHORIZATION_CHANGED'
  | 'NOTIFICATION_CREATED';

export interface RealtimeSignal {
  kind: RealtimeSignalKind;
  tenantId: string;
  at: string;
}

/**
 * B6.17 — one shared socket for the whole tenant app.
 *
 * ── WHAT ARRIVES HERE IS A SIGNAL, NOT DATA ─────────────────────────────────
 * The server sends `{ kind, tenantId, at }` and nothing else. This hook reacts
 * by invalidating React Query keys; the refetch then goes through the normal
 * permission-guarded REST endpoint. That is what keeps RBAC, tenant isolation
 * and count/list consistency intact — see backend `realtime.contract.ts`.
 *
 * Never render anything from a signal. If you find yourself wanting a field on
 * it, add a query, not a payload.
 *
 * ── ONE CONNECTION, NOT ONE PER COMPONENT ───────────────────────────────────
 * Mounted once, high in the tree (AppLayout). Mounting it in several components
 * would open several sockets per tab. `socket.io-client` handles reconnection,
 * backoff and resubscription itself, so there is no retry logic here.
 *
 * ── CACHE KEYS ──────────────────────────────────────────────────────────────
 * `['dashboard-operations']` is reused deliberately — it is the key the OPS
 * dashboard, work queue, review workspace and assignment center already share,
 * and B6.17 Part 2 requires not creating a competing key for the same state.
 * Invalidation is cheap when nothing is mounted: React Query only refetches
 * observers that actually exist, so a signal arriving while the user is on
 * another page costs nothing.
 */
/**
 * D-112 Stage 4 — the queries an authorization change can invalidate.
 *
 * Exactly the set `AuthorizationCaseDetailPage` already refreshes after its own
 * mutations, plus the radar's journey. Declared here so "the authorization
 * queries" has one definition rather than two that can drift apart.
 *
 * Note these are three DISTINCT prefixes, not nested ones: React Query matches
 * key segments whole, so `['authorization-case']` does not match
 * `['authorization-case-journey', id]` and neither matches
 * `['authorization-cases', …]`.
 */
const AUTHORIZATION_QUERY_KEYS = [
  ['authorization-case-journey'],
  ['authorization-case'],
  ['authorization-cases'],
] as const;

export function useRealtime(): void {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || accessToken === null) return;

    const socket: Socket = io('/realtime', {
      // Token travels in the handshake auth payload, never in the query string —
      // query strings end up in proxy and server access logs.
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    const onSignal = (signal: RealtimeSignal): void => {
      if (!signal?.kind) return;

      // Every kind can change the OPS dashboard: the report queue, the
      // inspection queue, the lifecycle chart and the activity feed all live
      // behind this one key.
      void queryClient.invalidateQueries({ queryKey: ['dashboard-operations'] });

      // The bell badge and the panel.
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });

      // A report decision or a lifecycle transition also changes the inspection
      // detail screen the actor may be looking at.
      // ── D-112 Stage 4 — the Authorization Radar ─────────────────────────
      //
      // Invalidation only. Nothing is read off the signal and nothing is written
      // into the cache from it: the journey — including which case moved, where
      // it went and whether replay still agrees with live state — comes back
      // through `GET /authorization-cases/:id/journey`, which re-runs the §16
      // integrity check on every refetch. A cache written from a socket payload
      // would be a second, unverified source of journey truth (Rule I, OD-R4).
      //
      // The key prefix is invalidated WITHOUT an id because the signal carries
      // none by design. That is not a broad sweep in practice: React Query
      // refetches only queries with a live observer, and at most one journey is
      // mounted at a time — the case detail page the operator has open. A
      // journey sitting cold in the cache is marked stale and refetched if and
      // when it is next displayed.
      //
      // The three keys are exactly the set `AuthorizationCaseDetailPage`
      // already treats as "the authorization queries" after its own mutations;
      // this reuses that grouping rather than inventing a competing one.
      if (signal.kind === 'AUTHORIZATION_CHANGED') {
        for (const queryKey of AUTHORIZATION_QUERY_KEYS) {
          // ── The initial-load race, and why a bare invalidate is not enough ──
          //
          // If a query is ALREADY fetching when the signal lands — the page was
          // opened a moment before someone else moved the case — React Query
          // dedupes the invalidation's refetch into that pending request rather
          // than issuing a new one. When the pending (pre-change) response then
          // resolves, it is committed AND `isInvalidated` is cleared. The result
          // is a radar showing the old journey with nothing left to correct it:
          // no stale flag, no retry, no second signal coming.
          //
          // Verified against @tanstack/react-query 5.101.2 rather than assumed;
          // `cancelRefetch: true` does not help, because there is no abortable
          // retryer to cancel — only an already-pending promise to await. The
          // fix is to notice the in-flight case and refetch once it settles.
          const wasFetching =
            queryClient.getQueryCache().findAll({ queryKey, fetchStatus: 'fetching' }).length > 0;

          const invalidated = queryClient.invalidateQueries({ queryKey });

          if (wasFetching) {
            void invalidated.then(() => {
              void queryClient.refetchQueries({ queryKey });
            });
          } else {
            void invalidated;
          }
        }
      }

      if (
        signal.kind === 'REPORT_REVIEWED' ||
        signal.kind === 'LIFECYCLE_CHANGED' ||
        signal.kind === 'CUSTOMER_DECISION' ||
        signal.kind === 'INSPECTION_ASSIGNED'
      ) {
        void queryClient.invalidateQueries({ queryKey: ['inspection'] });
        // B6.19 — a report decision or a customer response can flip whether the
        // job is closable, so the Close button must re-evaluate. Without this the
        // action would appear only after a manual reload, which is the exact
        // problem B6.17 set out to remove.
        void queryClient.invalidateQueries({ queryKey: ['inspection-closure-eligibility'] });
      }
    };

    socket.on(REALTIME_EVENT, onSignal);

    return () => {
      socket.off(REALTIME_EVENT, onSignal);
      socket.disconnect();
    };
    // Reconnects with a fresh token after rotation, which is correct: the old
    // socket is torn down and a new authenticated one replaces it.
  }, [accessToken, isAuthenticated, queryClient]);
}
